import { labelInLocale } from "../i18n/localizedLabels";
import { tMessage } from "../i18n/runtime";
import { getLocale, translateMessage } from "../i18n/runtime";
import { t } from "../i18n/runtime";
import { useCallback, useEffect, useMemo, useRef } from "react";
import type { PointerEvent as ReactPointerEvent } from "react";
import { GOOD_TYPES } from "@cwb/core";
import type {
  BrokerState,
  GoodType,
  MarketOrder,
  NationId,
  Shipment,
} from "@cwb/core";
import type { DynamicNation } from "@cwb/protocol";
import { nationColorCss, useGameStore } from "../stores/gameStore";
import { gameBridge } from "../game/GameBridge";
import {
  MAX_TRADE_QUANTITY,
  brokerGift,
  marketBuy,
  marketSell,
} from "../network/api";
import type { SellTerms } from "../network/api";
import {
  CREDIT,
  GOOD,
  goodAmount,
  translateError,
  withLocaleParticle,
} from "../i18n";
import { useActiveNationId } from "./NationPanel";
import { GoodIcon } from "./GoodIcon";
import { NationFlag } from "./NationFlag";
import { WealthChart } from "./WealthChart";
import { holdingOf } from "./holdings";
import { Button, Stat, formatMoney, formatMoneyShort } from "./primitives";
import type { Tone } from "./primitives";

/**
 * The counterparty's order book — the centre of the player's game.
 *
 * Order sides are stored from the NATION's point of view. The panel is written
 * from the player's: the nation's BUY orders are its 수요, and the player sells
 * into them; the nation's SELL orders are its 공급, and the player buys them.
 * One nation at a time, named in the header, so there is never a question about
 * who is on the other side of the trade.
 *
 * Every button moves exactly one unit. Batch size was a decision the player had
 * to make before they could make the interesting one — who to trade with, and
 * at whose price — so the panel makes it for them and lets them click again. The
 * clicks are the player's and the requests are the network's, so a burst of the
 * first is collected into one of the second: what limits how fast the player
 * trades should be their hand, not a round trip.
 *
 * A demand row whose buyer cannot pay for its own order out of the treasury is
 * where the credit feature lives. It offers two trades rather than one: the unit
 * paid for in cash while the treasury lasts, and the same unit booked against
 * the credit line. The terms of the line are not on the row: they are a property
 * of the nation, not of the order, and printing them on every row said the same
 * sentence four times over and pushed the figures that differ apart. They are
 * read once, on the counterparty ledger directly above the buttons that draw on
 * them — 미수금 carries what is owed over the line it is owed against.
 */

/** One click, one unit — the panel never asks the player for a batch size. */
const TRADE_UNITS = 1;

/**
 * The one blocked reason the panel keeps off the row. Money is not a property
 * of the good in front of you, it is a property of you, so an empty wallet is
 * said once over the ledger it belongs to instead of being repeated under every
 * product the wallet cannot reach.
 */
export const cashShortText = (locale = getLocale()): string =>
  translateMessage(locale, "TradePanel.not_enough_cash", []);

/**
 * How long clicks pile up before the burst goes out as a single order. Long
 * enough that mashing costs one request instead of twenty, short enough that a
 * lone click still reaches the server while the player is looking at the row.
 */
export const COALESCE_MS = 120;

/**
 * The longest a banked click may wait, however fast the player keeps clicking.
 *
 * Without it the window is a pure trailing debounce: each click cancels the
 * previous timer, so a continuous mash never produces the 120 ms of silence the
 * timer needs and nothing is ever sent. The ceiling is what makes a held-down
 * burst arrive in pieces rather than all at the end.
 *
 * It has to stay under {@link HOLD_REPEAT_MS}, and that is not a preference.
 * A hold opens at 300 ms — slower than the debounce — so the first clicks each
 * flush on their own and the player sees every one land. As the hold ramps past
 * the debounce the clicks start banking, and from then on the ceiling alone
 * decides how often anything visibly happens. At 600 ms the button was buying
 * three times as fast by the third second while the screen answered a third as
 * often, which reads exactly like a control that is slowing down. Under the
 * opening gap, the answer rate never falls below where it started.
 * `tests/tradePanel.test.ts` walks the ramp and holds that line.
 */
export const COALESCE_MAX_MS = 200;

/** Clicks banked on one row, in one payment mode, waiting for the window to close. */
export interface BankedClicks {
  units: number;
  /** When the first click of this burst landed, for the max-wait ceiling. */
  openedAt: number;
}

/** What one click does to the bank it lands on. */
export interface ClickPlan {
  /** Send what is already banked first: this click will not fit in it. */
  flushFirst: boolean;
  units: number;
  openedAt: number;
  /** Milliseconds before the bank goes out. */
  wait: number;
}

/**
 * Where one click goes. Pure and exported because the ceiling is the whole of
 * it and it fails silently: a pure trailing debounce shipped once, and a player
 * who never stopped clicking never sent a single order — every click cancelled
 * the timer that would have sent the last one.
 *
 * The bank is never shown. A visible "waiting" count turned a burst of clicks
 * into something the player had to watch instead of a row they were trading on,
 * so clicks that cannot go out yet join the bank and say nothing.
 */
export function planClick(banked: BankedClicks | null, now: number): ClickPlan {
  // One request carries a bounded quantity; a burst past it becomes two.
  const flushFirst =
    banked !== null && banked.units + TRADE_UNITS > MAX_TRADE_QUANTITY;
  const carried = flushFirst ? null : banked;
  const openedAt = carried?.openedAt ?? now;
  // Whatever is left of the debounce window, but never past the ceiling — so a
  // player holding the button down sees goods move rather than nothing
  // happening until they stop.
  const wait = Math.max(
    0,
    Math.min(COALESCE_MS, openedAt + COALESCE_MAX_MS - now),
  );
  return {
    flushFirst,
    units: (carried?.units ?? 0) + TRADE_UNITS,
    openedAt,
    wait,
  };
}

/** A bank with the timer that will send it — the live form, held in a ref. */
interface Batch extends BankedClicks {
  timer: number;
}

/** Cash and credit are different orders to the server, so they bank apart. */
const batchKey = (orderId: string, terms: SellTerms): string =>
  `${orderId}|${terms}`;

/**
 * Whole units a budget covers, by the same rule the core fills an order with —
 * including its correction step, so the panel never offers a unit the server
 * would strip back off the fill.
 */
function affordableUnits(funds: number, unitPrice: number): number {
  if (funds <= 0) return 0;
  let units = Math.floor(funds / unitPrice);
  if (units * unitPrice > funds) units -= 1;
  return Math.max(0, units);
}

/**
 * The trade response carries no shipment id, so the convoy is picked up from
 * the first patch that contains a shipment we had not seen when we fired.
 */
function traceNewShipment(nationId: NationId, goodType: GoodType): void {
  const seen = new Set(
    (useGameStore.getState().state?.shipments ?? []).map((s) => s.id),
  );
  let timer = 0;
  const stop = gameBridge.subscribe((s) => {
    const found = s.state?.shipments.find(
      (sh) =>
        !seen.has(sh.id) &&
        sh.nationId === nationId &&
        sh.goodType === goodType,
    );
    if (!found) return;
    gameBridge.traceShipment(found.id);
    window.clearTimeout(timer);
    stop();
  });
  timer = window.setTimeout(stop, 5000);
}

/**
 * What the credit line would carry on this row. Built whenever the treasury
 * falls short of the order in front of it, and describing the single unit a
 * click moves — the nation that needs propping up is the one that cannot pay for
 * what it is asking for, not the one already down to its last coin.
 */
interface CreditTerms {
  /** Units the treasury still covers on its own: the clicks before the book opens. */
  cashUnits: number;
  /** Of one unit's price, the part the treasury cannot cover. */
  onCredit: number;
  /** True when treasury and line together cannot reach a single unit. */
  exhausted: boolean;
}

/**
 * Why the row's buttons are dead, or null when the trade is on. Pure and
 * exported: this is the rule that decides whether the player can act at all,
 * and it regresses without a symptom — a button that quietly stops working
 * looks exactly like a market with nothing worth trading in it.
 *
 * The sell side asks what the broker OWNS, through the same `holdingOf` the
 * warehouse readout uses. A gate that counted only landed stock read 재고 없음
 * on a row while the readout overhead said 무기 20 (수송 20), and the two
 * numbers were describing the same crates. Stock on the road arrives and then
 * leaves again; there is nothing to protect the player from.
 *
 * The buy side still counts inbound convoys against the hold, because the core
 * reserves that space too — offering a purchase the server has already
 * committed the room to is the one direction where the reservation is real.
 */
export function blockedReason(
  order: MarketOrder,
  broker: BrokerState,
  shipments: readonly Shipment[],
  quantity: number,
): string | null {
  if (order.side === "SELL") {
    const cost = quantity * order.unitPrice;
    if (broker.cash < cost)
      return tMessage("TradePanel.needs", (_locale) => ({
        p0: cashShortText(_locale),
        p1: formatMoneyShort(cost),
      }));
    return null;
  }
  if (holdingOf(order.goodType, broker.warehouse, shipments).total < quantity) {
    return tMessage("TradePanel.no_stock_no_in_the_warehouse", (_locale) => ({
      p0: withLocaleParticle(
        labelInLocale(GOOD, order.goodType, _locale) ?? order.goodType,
        "이가",
        _locale,
      ),
    }));
  }
  return null;
}

/**
 * How this order's price compares with the going rate for its kind of trade, and
 * whether that is good news for the player.
 *
 * 시세 here is {@link marketPrice} — the mean of every live order on this good,
 * both sides together — so 100% is the market and the number says where this row
 * sits in it, without the player having to remember what weapons went for a
 * minute ago.
 *
 * The COLOUR is judged against a different figure: the average of this row's own
 * side. One shared 시세 cannot tint, because the two columns never overlap — every
 * supply row sits under the book average and every demand row over it, so a tone
 * taken from the printed percentage would be the same colour all the way down
 * both columns. Against its own side, a row is dearer or cheaper than the ones
 * the player could take instead, which is the question the colour is for.
 *
 * Which direction is good depends on which side of the row the player is on, and
 * it is exactly opposite: the player SELLS into a nation's BUY order, so dear is
 * a good sale; the player BUYS from a nation's SELL order, so dear is a bad
 * purchase. Tinting on the wrong one of those would be worse than not tinting at
 * all.
 */
export interface MarketMark {
  percent: number;
  tone: "good" | "bad" | "flat";
  title: string;
}

/**
 * The average of the live orders on ONE side of a good — what the rows the
 * player could take INSTEAD of this one are charging.
 *
 * The colour is judged against this and the printed percentage is not, because
 * the two questions are different. "What is this worth" is answered by the whole
 * book; "is this a good one" is only answerable against the rows it competes
 * with, and a supply row only ever competes with other supply rows.
 *
 * Measured on a mock round, each column sits within about 3% of its own average
 * — ore supply 97.1 to 101.5, weapons supply 97.3 to 102.3 — which is why the
 * tone has no dead band. An 8-point one, which is what the shared figure used,
 * left every supply row on the board colourless: 6 of 6 ore rows and 8 of 8
 * weapons rows. Above the side's average or below it is the whole rule.
 */
export function sideAveragePrice(
  orders: readonly MarketOrder[],
  goodType: GoodType,
  side: MarketOrder["side"],
): number | null {
  return weightedPrice(
    orders.filter((o) => o.side === side),
    goodType,
  );
}

/**
 * A price average weighted by how much is actually on offer at it.
 *
 * `remainingQuantity` used to be read only as "is this order still live" and
 * never as a weight, so an order for four crates counted exactly as much as one
 * for sixty. On a real book that is not a rounding difference. Measured at tick
 * 1200 of a round, the weapons market held eight supply orders totalling 392
 * crates around 18,000 and four demand orders totalling 19 crates around 54,000
 * — an unweighted mean called that market 30,062 when almost nothing on it could
 * be had at anywhere near that. Weighted it is 19,750.
 *
 * The consequence is worth being plain about: the figure now follows whichever
 * side of the book holds the goods. That is what a market price is — if 95% of
 * what exists is offered at 18,000 then 18,000 is what the thing is worth — but
 * it does mean 시세 sits near the supply price while supply is deep, and the
 * demand column reads as a large multiple of it rather than a small one.
 */
function weightedPrice(
  orders: readonly MarketOrder[],
  goodType: GoodType,
): number | null {
  let value = 0;
  let volume = 0;
  for (const order of orders) {
    if (order.goodType !== goodType || order.remainingQuantity <= 0) continue;
    value += order.unitPrice * order.remainingQuantity;
    volume += order.remainingQuantity;
  }
  return volume > 0 ? value / volume : null;
}

/**
 * The going rate for a good: every live order on it, both sides together,
 * weighted by how much each is offering.
 *
 * One number per good, which is what a 시세 is. A row is read against the whole
 * market rather than against its own column, so 5,600 for ore when the board's
 * goods average 9,000 reads as 62% — a fact about the trade itself, not about
 * how it ranks among five other sellers who are all glutted for the same reason.
 *
 * Weighted by quantity, not a plain mean over orders: see `weightedPrice`. The
 * count of orders is not the size of a market and reading it as one put the
 * weapons 시세 34% high.
 *
 * Not a median either: with both sides in one pool a median lands wherever the
 * larger side happens to be, so a good with six supply rows and four demand rows
 * would call the supply price "the market" on a headcount rather than on what is
 * actually there.
 *
 * This replaces a per-side median, and the trade is worth stating because it
 * comes back the moment anyone reads the colours. The two sides do not overlap:
 * measured over a round, ore supply sat 5,610-5,865 while ore demand sat
 * 12,351-19,472, because a state posts a SELL only when it is glutted and a BUY
 * only when it is short. Against one shared number every supply row is under
 * 100 and every demand row over it, so the PERCENTAGE separates rows within a
 * column while the TONE no longer does. The per-side median was the reverse: it
 * made 100% the going rate on each side by construction, so both colours always
 * appeared, but it could never say a row was cheap in absolute terms.
 */
export function marketPrice(
  orders: readonly MarketOrder[],
  goodType: GoodType,
): number | null {
  return weightedPrice(orders, goodType);
}

export function marketMark(
  unitPrice: number,
  goingRate: number,
  sideRate: number,
  playerBuys: boolean,
): MarketMark | null {
  if (!Number.isFinite(goingRate) || goingRate <= 0) return null;
  const percent = Math.round((unitPrice / goingRate) * 100);

  // The number is this row against the whole market. The colour is this row
  // against the rows it competes with, which are the ones on its own side.
  // Compared as prices rather than as rounded percentages: the columns sit
  // within a few points of their own average, and rounding to whole percent
  // would tie rows that are genuinely apart.
  const against =
    Number.isFinite(sideRate) && sideRate > 0 ? sideRate : goingRate;
  const dearer = unitPrice - against;
  // Cheap is good when buying, dear is good when selling.
  const favourable = playerBuys ? -dearer : dearer;
  const tone = dearer === 0 ? "flat" : favourable > 0 ? "good" : "bad";
  const title = playerBuys
    ? t("TradePanel.this_is_of_the_average_price_across", { p0: percent })
    : t("TradePanel.this_is_of_the_average_price_across_5", { p0: percent });
  return { percent, tone, title };
}

/**
 * How a held-down trade button repeats. A click is one unit, so holding is how
 * the player buys forty of something without clicking forty times — the batch
 * size they were never asked for, expressed as how long they lean on it.
 *
 * It accelerates. A flat rate has to be either too slow for a big position or
 * too fast to stop on a small one; ramping means the first few units are
 * placed deliberately and a long hold still clears a hundred.
 */
export const HOLD_REPEAT_MS = 300;
export const HOLD_REPEAT_MIN_MS = 100;
export const HOLD_REPEAT_STEP_MS = 20;

/** The gap before the next repeat, given the gap before this one. */
export function nextHoldDelay(current: number): number {
  return Math.max(HOLD_REPEAT_MIN_MS, current - HOLD_REPEAT_STEP_MS);
}

/**
 * Press-and-hold, as a pointer handler.
 *
 * Fires once on the way down so a tap is still a click, then repeats until the
 * pointer lifts, leaves, or is cancelled. It also stops on window blur: a
 * pointerup delivered to another window never reaches this element, and a button
 * that kept buying after the player alt-tabbed would empty their account.
 */
function useHold(fire: () => void): {
  onPointerDown: (event: ReactPointerEvent<HTMLButtonElement>) => void;
  onPointerUp: () => void;
  onPointerLeave: () => void;
  onPointerCancel: () => void;
} {
  const timer = useRef(0);
  const fireRef = useRef(fire);
  fireRef.current = fire;

  const stop = useCallback(() => {
    if (timer.current === 0) return;
    // A chain of timeouts, not an interval: the gap changes between firings.
    window.clearTimeout(timer.current);
    timer.current = 0;
  }, []);

  useEffect(() => {
    window.addEventListener("blur", stop);
    return () => {
      window.removeEventListener("blur", stop);
      stop();
    };
  }, [stop]);

  const start = useCallback(
    (event: ReactPointerEvent<HTMLButtonElement>) => {
      // Primary button only, and never on a disabled control.
      if (event.button !== 0 || event.currentTarget.disabled) return;
      fireRef.current();
      stop();
      let delay = HOLD_REPEAT_MS;
      const tick = (): void => {
        fireRef.current();
        delay = nextHoldDelay(delay);
        timer.current = window.setTimeout(tick, delay);
      };
      timer.current = window.setTimeout(tick, delay);
    },
    [stop],
  );

  return {
    onPointerDown: start,
    onPointerUp: stop,
    onPointerLeave: stop,
    onPointerCancel: stop,
  };
}

interface RowPropsBase {
  order: MarketOrder;
  onTrade: (terms: SellTerms) => void;
  blocked: string | null;
  credit: CreditTerms | null;
  /** Mean of every live order on this good, both sides — what the % is against. */
  goingRate: number;
  /** Mean of the live orders on this row's own side — what the colour is against. */
  sideRate: number;
}

/**
 * One button, three rungs. A sale stays a sale while the buyer can pay for it;
 * when the treasury cannot reach a single unit it becomes a credit sale, and
 * when the credit line cannot reach one either it becomes a gift.
 *
 * They used to stand side by side, and the second was dead almost all of the
 * time — the row asked the player to read two controls to find the one that
 * worked. Which rung applies is a property of the buyer, not a choice the
 * player should have to make, so the row works it out and offers that one.
 */
export function sellRung(credit: CreditTerms | null): SellTerms {
  if (credit === null || credit.cashUnits >= TRADE_UNITS) return "cash";
  return credit.exhausted ? "gift" : "credit";
}

/** The quantity colour mirrors whether this row's action button can be used. */
export type TradeAvailabilityTone = "ready" | "blocked";

export function tradeAvailabilityTone(
  disabled: boolean,
): TradeAvailabilityTone {
  return disabled ? "blocked" : "ready";
}

interface RowAction {
  label: string;
  tone: "buy" | "sell" | "danger" | "gift";
  terms: SellTerms;
  hint: string;
}

interface RowProps extends RowPropsBase {
  /**
   * How many of this order could actually be collected from the selected ground.
   *
   * An order is posted by a nation and its quantity is the nation's, but the
   * crates come off the province sent as `fromProvinceId` — so a row claiming
   * 43 against selected ground holding 6 is quoting a number no click can reach.
   *
   * Selling INTO a nation is not limited this way: the convoy carries what the
   * broker holds, and where it unloads does not cap it.
   */
  available: number;
}

function TradeRow({
  order,
  onTrade,
  blocked,
  credit,
  goingRate,
  sideRate,
  available,
}: RowProps) {
  const playerBuys = order.side === "SELL";
  const market = marketMark(order.unitPrice, goingRate, sideRate, playerBuys);
  // Holding the button is how a batch gets bought — see `useHold`. `onClick` is
  // gone rather than kept alongside it: pointerdown already fires the first
  // unit, and leaving both would buy two for every tap.
  const hold = useHold(() => onTrade(action.terms));
  const remaining = Math.floor(available);
  const disabled = blocked !== null || remaining < 1;

  const rung = sellRung(credit);
  const sellAction: RowAction =
    rung === "cash"
      ? {
          label: t("TradePanel.sell"),
          tone: "sell",
          terms: "cash",
          hint: t("TradePanel.sell_7", {
            p0: formatMoneyShort(order.unitPrice),
          }),
        }
      : rung === "credit"
        ? {
            label: CREDIT.sellOnCredit,
            tone: "danger",
            terms: "credit",
            hint:
              credit && credit.onCredit > 0
                ? t("TradePanel.the_treasury_cannot_pay_for_one_unit", {
                    p0: formatMoneyShort(credit.onCredit),
                  })
                : t("TradePanel.the_treasury_is_empty_one_unit_will"),
          }
        : {
            label: t("TradePanel.gift"),
            // Not the credit red. 외상 and 증여 both hand goods to a nation that
            // cannot pay, and in the same red they read as one escalating
            // control — but one books a receivable and the other writes the
            // goods off, and that is the whole decision on the row.
            tone: "gift",
            terms: "gift",
            hint: t("TradePanel.treasury_and_credit_are_exhausted_give_one"),
          };

  const action: RowAction = playerBuys
    ? {
        label: t("TradePanel.buy"),
        tone: "buy",
        terms: "cash",
        hint: t("TradePanel.buy_13", { p0: formatMoneyShort(order.unitPrice) }),
      }
    : sellAction;
  const availability = tradeAvailabilityTone(disabled);
  const stockLimited = remaining < Math.floor(order.remainingQuantity);
  const availabilityTitle =
    blocked ??
    (playerBuys
      ? stockLimited
        ? t("TradePanel.the_order_has_units_but_the_selected", {
            p0: Math.floor(order.remainingQuantity),
            p1: remaining,
          })
        : t("TradePanel.selected_province_stock_and_your_cash_are")
      : rung === "cash"
        ? t("TradePanel.the_nation_can_pay_cash_available_to")
        : rung === "credit"
          ? t("TradePanel.the_nation_lacks_cash_sell_on_credit")
          : t("TradePanel.the_nation_has_no_cash_or_credit"));

  return (
    <div
      className={`${playerBuys ? "trade-row trade-row--supply" : "trade-row trade-row--demand"} trade-row--${order.goodType}`}
    >
      {/*
       * How many they want is the trade; the price only separates a good one
       * from a bad one. So the count leads at display size and the unit price
       * trails it, small and dim — the row reads "소총 43개" first and answers
       * "at what?" second. The agent's note on its own order used to sit under
       * this and has been cut: nobody read it and it pushed the figures apart.
       */}
      {/*
        The mark, then the word, then what one costs and how that compares
        with the market — and only then how many are on offer.

        The price is written out in full rather than as $24K. A broker's whole
        job is the gap between two prices, and the rounded thousand is exactly
        the digit that gap lives in.
      */}
      <div className="trade-row__main">
        <span className={`trade-row__good trade-row__good--${order.goodType}`}>
          <GoodIcon good={order.goodType} />
          {GOOD[order.goodType]}
        </span>
        <span className="trade-row__unit num">
          {formatMoney(order.unitPrice)}{" "}
          <span className="trade-row__per">{t("TradePanel.unit")}</span>
          {market === null ? null : (
            <span
              className={`trade-row__market trade-row__market--${market.tone}`}
              title={market.title}
            >
              {t("TradePanel.market")} {market.percent}%
            </span>
          )}
        </span>
        <span
          className={`trade-row__qty trade-row__qty--${availability} num`}
          title={availabilityTitle}
        >
          <strong>{remaining}</strong>
          <span className="trade-row__qty-unit">{t("TradePanel.units")}</span>
        </span>
      </div>

      {/* Hard right, in its own column, so every row on the panel has its
          control in the same place whatever the figures beside it do. */}
      <div className="trade-row__act">
        <span
          className="trade-row__action-wrap"
          tabIndex={disabled ? 0 : undefined}
          aria-label={
            disabled ? `${action.label}: ${blocked ?? action.hint}` : undefined
          }
        >
          <Button tone={action.tone} small disabled={disabled} {...hold}>
            {action.label}
          </Button>
          <span className="trade-row__action-tooltip" role="tooltip">
            {blocked ?? action.hint}
          </span>
        </span>
      </div>
    </div>
  );
}

/**
 * The two ledgers, one above the other, column for column.
 *
 * Mine and theirs carry the same four things in the same four places — stock,
 * stock, money, what is owed — so the comparison the whole panel exists to
 * support is a vertical glance rather than a memory exercise. The arrow between
 * them is the only mark on the panel that says what the panel is FOR: these two
 * columns of numbers can be moved into each other.
 *
 * My stock is what I own, convoys included, because that is what the sell
 * buttons below will actually let me part with.
 */
interface LedgerProps {
  nation: DynamicNation;
  name: string;
  /** Their colour, because their name now heads their half of the swap. */
  color: number | undefined;
  /** The ground being traded with. Its stock is the stock that can move. */
  ground: TradeGround | null;
  /** Cheapest thing on offer, so the wallet can say it cannot reach it. */
  cheapestAsk: number | null;
  /** Cheapest thing they are bidding for, so their treasury can say the same. */
  cheapestBid: number | null;
}

/**
 * How close a nation is to having no credit left.
 *
 * The dossier used to draw this as a bar, and the bar was the point of it: a
 * client at 0.9 is one lost province away from an exhausted line, and the player
 * has to know that before the next sale rather than after the write-off. The bar
 * is gone; the warning moves onto the figure.
 */
export function debtTone(nation: {
  debtToBroker: number;
  creditLimit: number;
}): Tone {
  if (nation.debtToBroker <= 0) return "neutral";
  const used =
    nation.creditLimit > 0 ? nation.debtToBroker / nation.creditLimit : 1;
  return used >= 0.85 ? "red" : "amber";
}

function Ledgers({
  nation,
  name,
  color,
  ground,
  cheapestAsk,
  cheapestBid,
}: LedgerProps) {
  const broker = useGameStore((s) => s.state?.broker);
  const shipments = useGameStore((s) => s.state?.shipments);
  const receivables = useGameStore((s) => s.score?.receivables ?? 0);
  if (!broker) return null;

  const weapons = holdingOf("weapons", broker.warehouse, shipments ?? []);
  const resources = holdingOf("resources", broker.warehouse, shipments ?? []);

  // Two shortages, one on each side of the swap, each said on the figure that
  // is short rather than on the goods it cannot reach. The rows below stay
  // about goods; whose wallet is empty is a fact about the wallet.
  const brokeFor = cheapestAsk !== null && broker.cash < cheapestAsk;
  const theyAreBrokeFor = cheapestBid !== null && nation.treasury < cheapestBid;

  return (
    <div className="ledgers">
      <div className="ledger ledger--mine">
        <div className="ledger__who label">{t("TradePanel.my_assets")}</div>
        {/* The total, above the parts it is the total of. */}
        <WealthChart />
        {/*
          Money on the left, stock on the right, on both ledgers.

          The two halves answer different questions and the player asks them in
          that order: what can I spend, and then what have I got to move. Keeping
          the split in the same place on both rows is what lets the eye run down
          a column instead of hunting along a row.
        */}
        <div className="ledger__row">
          <Stat
            label={t("TradePanel.cash")}
            value={formatMoney(broker.cash)}
            tone={brokeFor ? "red" : "neutral"}
            big
            title={
              brokeFor
                ? t("TradePanel.no_cash_the_cheapest_offer_costs_but", {
                    p0: formatMoneyShort(cheapestAsk ?? 0),
                    p1: formatMoney(broker.cash),
                  })
                : undefined
            }
          />
          <Stat
            label={CREDIT.receivables}
            value={formatMoney(receivables)}
            tone={receivables > 0 ? "amber" : "neutral"}
            big
          />
          <Stat
            label={t("TradePanel.weapons_held")}
            value={weapons.total}
            tone="cyan"
            big
          />
          <Stat
            label={t("TradePanel.resources_held")}
            value={resources.total}
            tone="amber"
            big
          />
        </div>
      </div>

      {/* Not decoration: it is the sentence "these two can trade", and it is the
          only place the panel says so without words. */}
      <div className="ledgers__swap" aria-hidden="true">
        <span>⇅</span>
      </div>

      <div className="ledger ledger--theirs">
        {/* Their identity heads their own ledger rather than the panel: under
            the arrow the two halves read as one group — mine, the swap, theirs
            — instead of a title with two unrelated rows beneath it. */}
        <div className="trade-nation">
          <NationFlag
            nationId={nation.id}
            height={46}
            color={nationColorCss(color)}
          />
          <span className="trade-nation__name">{name}</span>
          <span className="trade-nation__tag">
            {t("TradePanel.trading_partner")}
          </span>
        </div>
        <div className="ledger__row">
          <Stat
            label={t("TradePanel.treasury")}
            value={formatMoney(nation.treasury)}
            tone={theyAreBrokeFor ? "red" : "neutral"}
            title={
              theyAreBrokeFor
                ? t("TradePanel.cannot_pay_cash_the_cheapest_order_costs", {
                    p0: name,
                    p1: formatMoneyShort(cheapestBid ?? 0),
                    p2: formatMoney(nation.treasury),
                  })
                : undefined
            }
          />
          {/*
           * What they owe over what they are allowed to owe.
           *
           * The line was only ever readable on the nation card, which no longer
           * carries it — and it is needed HERE, on the ledger with the credit
           * button under it, because "they cannot pay" and "they cannot borrow
           * either" are two different rows to press. The denominator is set
           * small and dim on purpose: it moves only when they gain or lose
           * ground, so it is background against a figure that moves per trade.
           */}
          <Stat
            label={CREDIT.receivables}
            value={
              <span className="credit-of">
                {formatMoney(nation.debtToBroker)}
                <span className="credit-of__limit">
                  / {formatMoney(nation.creditLimit)}
                </span>
              </span>
            }
            tone={debtTone(nation)}
            title={tMessage("TradePanel.used", (_locale) => ({
              p0: labelInLocale(CREDIT, "limit", _locale) ?? "limit",
              p1: formatMoney(nation.creditLimit),
              p2: formatMoney(nation.debtToBroker),
              p3: labelInLocale(CREDIT, "available", _locale) ?? "available",
              p4: formatMoney(nation.creditAvailable),
            }))}
          />
          {/* This ground's stock, not the nation's total — the total counted
              crates in provinces this trade will never touch. */}
          <Stat
            label={t("TradePanel.weapons_held_31")}
            value={ground ? ground.weapons : Math.round(nation.totalWeapons)}
            tone="cyan"
            title={
              ground
                ? t("TradePanel.weapons_stationed_in", { p0: ground.name })
                : undefined
            }
          />
          <Stat
            label={t("TradePanel.resources_held_33")}
            value={
              ground ? ground.resources : Math.round(nation.totalResources)
            }
            tone="amber"
            title={
              ground
                ? t("TradePanel.resources_stored_in", { p0: ground.name })
                : undefined
            }
          />
        </div>
      </div>
    </div>
  );
}

/**
 * 증여 — the crates leave and the ledger does not move.
 *
 * Its own block rather than a button on a demand row, because it is not a sale
 * on worse terms: it answers no order, so it is the only thing the player can
 * do for a nation that has stopped asking for anything, which is exactly what a
 * nation being overrun does. It takes the ground chosen at the top of the panel
 * like every other trade here.
 */
interface GiftBlockProps {
  nationId: NationId;
  nationName: string;
  ground: TradeGround | null;
}

function GiftBlock({ nationId, nationName, ground }: GiftBlockProps) {
  const sessionId = useGameStore((s) => s.sessionId);
  const broker = useGameStore((s) => s.state?.broker);
  const shipments = useGameStore((s) => s.state?.shipments);
  const showToast = useGameStore((s) => s.showToast);

  const send = useCallback(
    async (goodType: GoodType, provinceId: string, provinceName: string) => {
      if (!sessionId) return;
      const result = await brokerGift(
        sessionId,
        nationId,
        provinceId,
        goodType,
        TRADE_UNITS,
      );
      if (!result.ok) {
        showToast(translateError(result.error), "error");
        return;
      }
      showToast(
        tMessage("TradePanel.gift_of_to_accepted", (_locale) => ({
          p0: provinceName,
          p1: goodAmount(result.data.quantity, goodType, _locale),
        })),
        "success",
      );
      traceNewShipment(nationId, goodType);
    },
    [sessionId, nationId, showToast],
  );

  if (!broker) return null;

  return (
    <section className="trade-block trade-block--gift">
      <header className="trade-block__head">
        <span className="trade-block__title">
          {t("TradePanel.gift_give_without_payment")}
        </span>
        <span className="trade-block__hint">
          {ground
            ? t("TradePanel.to_the_selected_province")
            : t("TradePanel.no_recipient")}
        </span>
      </header>

      {GOOD_TYPES.map((goodType) => {
        const held = holdingOf(goodType, broker.warehouse, shipments ?? []);
        return (
          <GiftRow
            key={goodType}
            goodType={goodType}
            held={held.total}
            ground={ground}
            nationName={nationName}
            onGift={() => {
              if (ground) void send(goodType, ground.id, ground.name);
            }}
          />
        );
      })}
    </section>
  );
}

interface GiftRowProps {
  goodType: GoodType;
  held: number;
  ground: TradeGround | null;
  nationName: string;
  onGift: () => void;
}

function GiftRow({ goodType, held, ground, nationName, onGift }: GiftRowProps) {
  const hold = useHold(onGift);
  const short = held < TRADE_UNITS;
  const disabled = short || !ground;

  return (
    <div className="trade-row trade-row--gift">
      <div className="trade-row__main">
        <span className={`trade-row__good trade-row__good--${goodType}`}>
          <GoodIcon good={goodType} />
          {GOOD[goodType]}
        </span>
        <span className="trade-row__unit num">
          {t("TradePanel.held")} {held}
          <span className="trade-row__per"> {t("TradePanel.unit_40")}</span>
        </span>
      </div>
      <div className="trade-row__act">
        <Button
          tone="gift"
          small
          disabled={disabled}
          title={
            short
              ? tMessage(
                  "TradePanel.no_held_in_the_warehouse_or_in",
                  (_locale) => ({
                    p0: withLocaleParticle(
                      labelInLocale(GOOD, goodType, _locale) ?? goodType,
                      "이가",
                      _locale,
                    ),
                  }),
                )
              : ground
                ? t("TradePanel.give_one_unit_free_to_no_payment", {
                    p0: nationName,
                    p1: ground.name,
                  })
                : t("TradePanel.no_receiving_province")
          }
          {...hold}
        >
          {t("TradePanel.gift_44")}
        </Button>
      </div>
    </div>
  );
}

/**
 * The nation's ground, as the thing the player is trading with.
 *
 * A trade used to be with a nation, and a nation unloads everything at its
 * depot — so a sale meant for a province about to fall arrived in a warehouse
 * two provinces behind it, and a purchase could strip whichever province the
 * depot-first drain reached. The counterparty is a province now. The money is
 * still national: a nation has one treasury and many provinces, so cash is
 * central and crates are local.
 */
export interface TradeGround {
  id: string;
  name: string;
  weapons: number;
  resources: number;
  onFront: boolean;
  /** Pressure against this ground, 0..100. The reason to pick it. */
  pressure: number;
}

/** What is standing on the selected friendly ground. */
export interface GroundStock {
  weapons: number;
  resources: number;
}

/**
 * What one order could actually hand over right now.
 *
 * A purchase is collected from the province the player selected, so that pile
 * is the ceiling however large the national order is written. Every open sell
 * order on the same good draws on that selected pile.
 *
 * A sale is not capped this way. The crates are the broker's; the province is
 * only the address they are delivered to.
 */
export function availableFromGround(
  order: MarketOrder,
  ground: GroundStock | null,
): number {
  const remaining = Math.floor(order.remainingQuantity);
  if (order.side !== "SELL") return remaining;
  if (!ground) return 0;
  return Math.min(
    remaining,
    Math.floor(
      order.goodType === "weapons" ? ground.weapons : ground.resources,
    ),
  );
}

function useTradeGrounds(nationId: NationId | null): TradeGround[] {
  const provinces = useGameStore((s) => s.state?.provinces);
  const fronts = useGameStore((s) => s.state?.fronts);
  const mapProvinces = useGameStore((s) => s.map?.provinces);

  return useMemo<TradeGround[]>(() => {
    if (!nationId) return [];
    const names = new Map((mapProvinces ?? []).map((p) => [p.id, p.name]));
    // Pressure is stored on the front, not the province, so it is folded back
    // onto the ground being pushed: that is the number the player chooses on.
    const pressure = new Map<string, number>();
    for (const front of fronts ?? []) {
      for (const id of [front.provinceAId, front.provinceBId]) {
        pressure.set(id, Math.max(pressure.get(id) ?? 0, front.attackPressure));
      }
    }
    const out: TradeGround[] = [];
    for (const province of provinces ?? []) {
      if (province.ownerNationId !== nationId) continue;
      out.push({
        id: province.id,
        name: names.get(province.id) ?? province.id,
        weapons: Math.round(province.weapons),
        resources: Math.round(province.resources),
        onFront: pressure.has(province.id),
        pressure: Math.round(pressure.get(province.id) ?? 0),
      });
    }
    // Hardest-pressed first: the ground a delivery could still save leads.
    out.sort((a, b) => b.pressure - a.pressure || (a.id < b.id ? -1 : 1));
    return out;
  }, [provinces, fronts, mapProvinces, nationId]);
}

export function TradePanel() {
  const activeId = useActiveNationId();
  const sessionId = useGameStore((s) => s.sessionId);
  const orders = useGameStore((s) => s.state?.marketOrders);
  const shipments = useGameStore((s) => s.state?.shipments);
  const broker = useGameStore((s) => s.state?.broker);
  const nations = useGameStore((s) => s.state?.nations);
  const mapNations = useGameStore((s) => s.map?.nations);
  const showToast = useGameStore((s) => s.showToast);
  const selectedProvinceId = useGameStore((s) => s.selectedProvinceId);

  const grounds = useTradeGrounds(activeId);
  /**
   * The ground being traded with. It is the store's province selection, so
   * clicking the map and clicking a chip are the same act — and ground that
   * changed hands stops being the counterparty rather than silently sending
   * the next convoy to the enemy. Falls back to whatever is under the most
   * pressure, which is the head of the list.
   */
  const ground =
    grounds.find((g) => g.id === selectedProvinceId) ?? grounds[0] ?? null;
  /**
   * The banks themselves. A ref rather than state: a burst has to be read and
   * cleared in the same breath, and a render landing between those two would
   * send the same units twice.
   */
  const batches = useRef(new Map<string, Batch>());

  const mapNation = mapNations?.find((n) => n.id === activeId) ?? null;
  const nation = nations?.find((n) => n.id === activeId) ?? null;

  const { demand, supply } = useMemo(() => {
    const buys: MarketOrder[] = [];
    const sells: MarketOrder[] = [];
    for (const order of orders ?? []) {
      if (order.nationId !== activeId || order.remainingQuantity < 1) continue;
      (order.side === "BUY" ? buys : sells).push(order);
    }
    // Best terms first from the player's side: highest bid, cheapest ask.
    buys.sort((a, b) => b.unitPrice - a.unitPrice);
    sells.sort((a, b) => a.unitPrice - b.unitPrice);
    return { demand: buys, supply: sells };
  }, [orders, activeId]);

  /** Sends one burst: everything banked on this row and mode since the last send. */
  const flush = useCallback(
    async (key: string, order: MarketOrder, terms: SellTerms) => {
      const batch = batches.current.get(key);
      if (!batch) return;
      batches.current.delete(key);
      if (!sessionId) return;
      const units = batch.units;

      const playerBuys = order.side === "SELL";
      const provinceId = groundRef.current?.id;
      const result = playerBuys
        ? await marketBuy(sessionId, order.id, units, provinceId)
        : await marketSell(sessionId, order.id, units, terms, provinceId);

      if (!result.ok) {
        showToast(translateError(result.error), "error");
        return;
      }

      // The server answers with the fill it is queueing, not with one it has
      // made: the core runs the order on a later tick and has the last word on
      // it. 접수 is therefore the strongest thing the toast can honestly claim,
      // and the quantity is the server's rather than the one we asked for — a
      // burst that outran the treasury or the warehouse is cut down here.
      const action = (locale: string) =>
        playerBuys
          ? translateMessage(locale, "TradePanel.buy_45", [])
          : terms === "gift"
            ? translateMessage(locale, "TradePanel.gift_46", [])
            : terms === "credit"
              ? (labelInLocale(CREDIT, "sellOnCredit", locale) ??
                "sellOnCredit")
              : translateMessage(locale, "TradePanel.sell_47", []);
      const filled = result.data.quantity;
      const value = formatMoneyShort(result.data.totalValue);
      showToast(
        filled < units
          ? tMessage(
              "TradePanel.accepted_of_from_requested_units",
              (_locale) => ({
                p0: units,
                p1: goodAmount(filled, order.goodType, _locale),
                p2: action(_locale),
                p3: value,
              }),
            )
          : tMessage("TradePanel.accepted_of", (_locale) => ({
              p0: goodAmount(filled, order.goodType, _locale),
              p1: action(_locale),
              p2: value,
            })),
        filled < units ? "info" : "success",
      );
      traceNewShipment(order.nationId, order.goodType);
    },
    [sessionId, showToast],
  );

  /**
   * The ground as a ref, because `flush` fires from a timer that outlives the
   * render that scheduled it: closing over the value would send a burst to
   * whichever province was chosen when the first click landed.
   */
  const groundRef = useRef<TradeGround | null>(ground);
  groundRef.current = ground;

  /**
   * Takes a click and owes a request for it. Anything clicked before the window
   * closes joins the same order, so a mashing player spends one request per
   * burst and stays well inside the token bucket guarding the mutating routes.
   * A click that cannot go out yet is banked and nothing is said about it — the
   * row is a trade, not a queue the player has to watch drain.
   */
  const queue = useCallback(
    (order: MarketOrder, terms: SellTerms) => {
      if (!sessionId) return;
      const key = batchKey(order.id, terms);
      const banked = batches.current.get(key) ?? null;
      if (banked) window.clearTimeout(banked.timer);

      const plan = planClick(banked, performance.now());
      if (plan.flushFirst) void flush(key, order, terms);
      batches.current.set(key, {
        units: plan.units,
        openedAt: plan.openedAt,
        timer: window.setTimeout(
          () => void flush(key, order, terms),
          plan.wait,
        ),
      });
    },
    [sessionId, flush],
  );

  /** Why the button is dead. Short enough to sit next to it, not only in a tooltip. */
  const blockedFor = (order: MarketOrder, quantity: number): string | null => {
    if (!broker) return t("TradePanel.waiting_for_a_session");
    if (!ground) return t("TradePanel.no_province_available_to_trade");
    // The player buys off ONE province now, so the gate is that province's
    // stock rather than everything the nation holds behind its lines.
    if (order.side === "SELL") {
      const held =
        order.goodType === "weapons" ? ground.weapons : ground.resources;
      if (held < quantity)
        return t("TradePanel.no_stock_in", { p0: ground.name });
    }
    return blockedReason(order, broker, shipments ?? [], quantity);
  };

  /**
   * Credit terms for a demand row, or null when the nation can simply pay. The
   * trigger is the order, not the click: a buyer whose treasury cannot cover
   * what it is asking for is the buyer the player can prop up, and the offer has
   * to be on the row long before the treasury is short of a single unit.
   *
   * The split is derived the way `executeBrokerSell` derives it — the treasury
   * is spent first and only the remainder is lent — so a row that still has cash
   * for the unit under the cursor books nothing against the line for it.
   */
  const creditFor = (
    order: MarketOrder,
    buyer: DynamicNation,
  ): CreditTerms | null => {
    if (order.side !== "BUY") return null;
    const unitPrice = Math.max(order.unitPrice, 1);
    const remaining = Math.floor(order.remainingQuantity);
    if (buyer.treasury >= remaining * unitPrice) return null;

    return {
      cashUnits: affordableUnits(buyer.treasury, unitPrice),
      onCredit: unitPrice - Math.min(buyer.treasury, unitPrice),
      exhausted:
        affordableUnits(buyer.treasury + buyer.creditAvailable, unitPrice) <
        TRADE_UNITS,
    };
  };

  const rowsFor = (list: MarketOrder[], empty: string) => {
    if (list.length === 0) return <div className="trade-empty">{empty}</div>;
    return list.map((order) => (
      <TradeRow
        key={order.id}
        order={order}
        blocked={blockedFor(order, TRADE_UNITS)}
        credit={nation ? creditFor(order, nation) : null}
        goingRate={marketPrice(orders ?? [], order.goodType) ?? 0}
        sideRate={
          sideAveragePrice(orders ?? [], order.goodType, order.side) ?? 0
        }
        available={availableFromGround(order, ground)}
        onTrade={(terms) => queue(order, terms)}
      />
    ));
  };

  if (!mapNation || !nation) {
    return (
      <div className="trade-panel">
        <div className="trade-empty">
          {t("TradePanel.select_a_nation_on_the_map_to")}
        </div>
      </div>
    );
  }

  // The cheapest row on each side, so the two ledgers can say whose money ran
  // out without every row having to repeat it.
  const cheapestAsk = supply.length > 0 ? supply[0].unitPrice : null;
  const cheapestBid =
    demand.length > 0 ? demand[demand.length - 1].unitPrice : null;

  return (
    <div className="trade-panel">
      <Ledgers
        nation={nation}
        name={mapNation.name}
        color={mapNation.color}
        ground={ground}
        cheapestAsk={cheapestAsk}
        cheapestBid={cheapestBid}
      />

      <section className="trade-block trade-block--demand">
        <header className="trade-block__head">
          <span className="trade-block__title">
            {t("TradePanel.what_this_nation_wants_to_buy_demand")}
          </span>
          <span className="trade-block__hint">
            {ground
              ? t("TradePanel.deliver_to", { p0: ground.name })
              : t("TradePanel.sell_here")}
          </span>
        </header>
        {rowsFor(demand, t("TradePanel.no_current_buy_orders"))}
      </section>

      <section className="trade-block trade-block--supply">
        <header className="trade-block__head">
          <span className="trade-block__title">
            {t("TradePanel.what_this_nation_wants_to_sell_supply")}
          </span>
          <span className="trade-block__hint">
            {ground
              ? t("TradePanel.collect_from", { p0: ground.name })
              : t("TradePanel.buy_here")}
          </span>
        </header>
        {rowsFor(supply, t("TradePanel.no_current_sell_orders"))}
      </section>

      <GiftBlock
        nationId={mapNation.id}
        nationName={mapNation.name}
        ground={ground}
      />
    </div>
  );
}
